/**
 * F-10: the board rect worth mounting board-space overlays for — the visible
 * rect plus one viewport of buffer, recomputed from the camera store OUTSIDE
 * render and published as state ONLY once the camera leaves the rect it last
 * returned.
 *
 * The buffer is what makes that cheap AND correct: the returned rect always
 * contains the visible rect, so anything culled is provably off-screen, and a
 * pan can travel a whole viewport before this re-renders its layer. Pan must
 * stay at the compositor baseline (docs/10 §1) — subscribing to the camera per
 * frame here would put a React pass back into every pan frame, which is the one
 * thing windowing is explicitly NOT supposed to do.
 *
 * Shared by every layer that mounts DOM per board object (the floating elements
 * — F-30/F-32 — and the connectors between them — F-38), so they window on
 * exactly the same rect and cannot disagree about what is off-screen.
 */
import { useEffect, useState } from "react";

import type { CanvasStore } from "./canvas.js";
import { cullRect, cullRectContains, visibleBoardRect, type CullRect } from "./row-window.js";

export function useCullRect(canvas: CanvasStore): CullRect {
  const [rect, setRect] = useState<CullRect>(() =>
    cullRect(canvas.getState().camera, canvas.getState().viewport),
  );
  useEffect(() => {
    const update = () => {
      const { camera, viewport } = canvas.getState();
      setRect((prev) => {
        const view = visibleBoardRect(camera, viewport);
        // Still covered (a small pan, or a zoom IN): keep the rect, skip the
        // re-render entirely.
        return cullRectContains(prev, view) ? prev : cullRect(camera, viewport);
      });
    };
    update();
    return canvas.subscribe(update);
  }, [canvas]);
  return rect;
}
